import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import type { TagRow } from "./TagsData";

/**
 * Field spec for the Tags Manager free-text searchbar.
 *
 * `isViewingAllCustomers` mirrors the same flag used by Ports / FWaaS /
 * DCI list pages: when the SideNav lens is on the partner itself
 * (`state.selection.tenantId === state.selection.partnerId`) the table
 * shows an extra "Customer" column sourced from the embedded
 * `tenant.name`, and we expose the same column as a searchable field
 * here so the searchbar can filter by it ("Customer:CTX" or free text
 * "CTX"). When the lens is a single child tenant the field is hidden —
 * every row belongs to that one customer and the filter would be noise.
 */
export function makeTagsFields(
  isViewingAllCustomers = false
): FilterField<TagRow>[] {
  const fields: FilterField<TagRow>[] = [
    {
      label: "Tag",
      key: "name",
      accessor: (r) => r.name,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Description",
      key: "description",
      accessor: (r) => r.description ?? "",
      type: "string",
      inFreeText: true,
    },
    {
      label: "Category",
      key: "category",
      accessor: (r) => r.category?.name ?? "",
      type: "string",
      inFreeText: true,
    },
    {
      label: "Used in",
      key: "usedin",
      accessor: (r) => r.productSpread.join(" "),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Resources",
      key: "resources",
      accessor: (r) => r.resourceCount,
      type: "number",
    },
  ];

  if (isViewingAllCustomers) {
    // Insert right after "Tag" so the suggested-keys list groups the
    // identifier-like fields together — same placement convention as
    // `makePortFields` / `makeFwaasFields`.
    fields.splice(1, 0, {
      label: "Customer",
      key: "customer",
      accessor: (r) => r.tenant?.name ?? "",
      type: "string",
      inFreeText: true,
    });
  }

  for (const f of fields) f.key ??= labelId(f.label);
  return fields;
}
